/**
 * ManualButton — botón flotante (bottom-left) que abre el Manual General
 * scrolleado al módulo correspondiente a la página actual.
 *
 * Detecta `useLocation()` y mapea ruta → ancla del manual general.
 * Si no hay match, abre el índice del manual.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen, X, ExternalLink, Loader2 } from "lucide-react";
import api from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

// Mapeo de ruta → slug del ancla en el manual general
// (debe coincidir con los IDs auto-generados por markdown-it desde los headings H2/H3)
const ROUTE_TO_ANCHOR = [
  // Más específicas primero
  { match: /^\/(mi-ruta|carga-ruta)/, anchor: "8-montaje-en-obra", label: "Montaje en obra" },
  { match: /^\/r(\/|$)|^\/reportar/, anchor: "17-buscador-global-y-qr", label: "QR Reportar" },
  { match: /^\/calendario-rutas/, anchor: "7-rutas-y-logística", label: "Rutas" },
  { match: /^\/flota-mapa/, anchor: "7-rutas-y-logística", label: "Flota mapa" },
  { match: /^\/reparto-inteligente/, anchor: "7-rutas-y-logística", label: "Reparto inteligente" },
  { match: /^\/(rutas|ruta\/)/, anchor: "7-rutas-y-logística", label: "Rutas" },
  { match: /^\/vehiculos/, anchor: "19-administración-avanzada", label: "Vehículos" },
  { match: /^\/colaboradores/, anchor: "19-administración-avanzada", label: "Colaboradores" },

  { match: /^\/leads-analytics/, anchor: "18-rentabilidad-y-estadísticas", label: "Leads Analytics" },
  { match: /^\/leads/, anchor: "3-crm", label: "Leads" },
  { match: /^\/(clientes|cadenas|espacios|sub-clientes)/, anchor: "3-crm", label: "CRM" },

  { match: /^\/peticion/, anchor: "4-peticiones", label: "Peticiones" },
  { match: /^\/peticiones/, anchor: "4-peticiones", label: "Peticiones" },
  { match: /^\/mis-pendientes/, anchor: "4-peticiones", label: "Mis pendientes" },
  { match: /^\/pendientes-aprobacion/, anchor: "14-validaciones-y-supervisión", label: "Pendientes aprobación" },
  { match: /^\/tareas/, anchor: "2-dashboard-y-navegación", label: "Tareas" },

  { match: /^\/presupuesto/, anchor: "5-presupuestos", label: "Presupuestos" },

  { match: /^\/tesoreria/, anchor: "6-facturadirecta-y-tesorería", label: "Tesorería" },
  { match: /^\/facturas-externos/, anchor: "12-gastos", label: "Facturas externos" },
  { match: /^\/facturas/, anchor: "6-facturadirecta-y-tesorería", label: "FacturaDirecta" },
  { match: /^\/gestoria-inmatic/, anchor: "12-gastos", label: "Gestoría Inmatic" },
  { match: /^\/gastos/, anchor: "12-gastos", label: "Gastos" },
  { match: /^\/inversion-personal/, anchor: "19-administración-avanzada", label: "Inversión personal" },

  { match: /^\/(almacen|materiales|proveedores|catalogo-precios)/, anchor: "11-almacén-y-materiales", label: "Almacén" },

  { match: /^\/cierres-mensuales/, anchor: "14-validaciones-y-supervisión", label: "Cierres mensuales" },
  { match: /^\/(fichaje|fichajes-admin|rrhh|rrhh-admin)/, anchor: "13-rrhh", label: "RRHH" },

  { match: /^\/feedback/, anchor: "10-portal-feedback", label: "Feedback" },
  { match: /^\/(supervision|reportes|fotos-pendientes|validaciones-pendientes|albaranes)/, anchor: "14-validaciones-y-supervisión", label: "Validaciones" },

  { match: /^\/(portfolio|obras)/, anchor: "15-portales-del-cliente", label: "Portfolio" },
  { match: /^\/bandeja-ia/, anchor: "16-inteligencia-artificial", label: "Bandeja IA" },

  { match: /^\/(rentabilidad|estadisticas|productividad)/, anchor: "18-rentabilidad-y-estadísticas", label: "Rentabilidad" },

  { match: /^\/(usuarios|whatsapp|diagnostico)/, anchor: "19-administración-avanzada", label: "Admin" },

  // Default
  { match: /^\/$/, anchor: "2-dashboard-y-navegación", label: "Dashboard" },
];

function getAnchorForPath(pathname) {
  for (const rule of ROUTE_TO_ANCHOR) {
    if (rule.match.test(pathname)) return rule;
  }
  return null;
}

/** Convierte texto H2 ("## 6. FacturaDirecta y Tesorería") al ID que genera markdown-it */
function slugify(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "") // remove accents
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export default function ManualButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // No mostrar en páginas públicas, login, portales sin sesión o en /manuales (ya tienes el contenido)
  const hidden = useMemo(() => {
    const p = location.pathname;
    if (p === "/login") return true;
    if (p.startsWith("/p/")) return true; // portales públicos cliente
    if (p.startsWith("/portal-sub")) return true;
    if (p === "/manuales" || p.startsWith("/manuales/")) return true;
    return false;
  }, [location.pathname]);

  const rule = useMemo(() => getAnchorForPath(location.pathname), [location.pathname]);

  const cargarManual = useCallback(async () => {
    if (html) return; // ya cargado
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/manuales/general");
      setHtml(data.html || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "No se pudo cargar el manual");
    } finally {
      setLoading(false);
    }
  }, [html]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    await cargarManual();
  }, [cargarManual]);

  // Cuando carga el HTML y está abierto, hacer scroll al ancla
  useEffect(() => {
    if (!open || !html || !rule) return;
    // Esperar al próximo tick para que el DOM esté pintado
    const t = setTimeout(() => {
      const container = document.getElementById("manual-content-root");
      if (!container) return;
      // Buscar el H2 cuyo texto slugificado coincida con el ancla
      const headings = container.querySelectorAll("h2");
      for (const h of headings) {
        const id = slugify(h.textContent || "");
        if (id === rule.anchor) {
          h.scrollIntoView({ behavior: "smooth", block: "start" });
          // Resaltar brevemente
          h.style.transition = "background-color 0.6s ease";
          h.style.backgroundColor = "#fef3c7";
          setTimeout(() => { h.style.backgroundColor = ""; }, 1600);
          break;
        }
      }
    }, 100);
    return () => clearTimeout(t);
  }, [open, html, rule]);

  if (hidden) return null;

  return (
    <>
      {/* FAB bottom-left */}
      <button
        type="button"
        data-testid="manual-button-fab"
        onClick={handleOpen}
        title={rule ? `Ayuda: ${rule.label}` : "Manual de la app"}
        className="fixed bottom-5 left-5 z-40 flex items-center gap-2 rounded-full bg-zinc-900 hover:bg-zinc-800 text-amber-300 shadow-lg ring-1 ring-amber-500/30 transition-all hover:scale-105 px-4 py-2.5 group"
      >
        <BookOpen className="h-5 w-5" />
        <span className="text-sm font-medium hidden group-hover:inline lg:inline">
          {rule ? `Ayuda · ${rule.label}` : "Manual"}
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="manual-sheet"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-amber-600" />
              Manual de la App
            </SheetTitle>
            <SheetDescription>
              {rule
                ? <>Estás en <span className="font-semibold text-amber-700">{rule.label}</span>. Te he abierto el módulo correspondiente.</>
                : "Guía completa de RotulaciónDNF por módulos."}
              {" · "}
              <a
                href="/manuales"
                className="text-amber-700 underline hover:text-amber-800 inline-flex items-center gap-1"
                onClick={() => setOpen(false)}
              >
                Ver todos los manuales <ExternalLink className="h-3 w-3" />
              </a>
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                <span className="ml-2 text-zinc-500">Cargando manual…</span>
              </div>
            )}
            {error && (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {!loading && !error && html && (
              <>
                <style>{`
                  .manual-content h1 {
                    font-size: 1.5rem; font-weight: 700; color: #b45309;
                    border-bottom: 2px solid #fcd34d; padding-bottom: 0.5rem; margin-bottom: 1rem;
                  }
                  .manual-content h2 {
                    font-size: 1.125rem; font-weight: 600; color: #92400e;
                    margin-top: 1.5rem; margin-bottom: 0.5rem; scroll-margin-top: 1rem;
                    border-bottom: 1px solid #fde68a; padding-bottom: 0.25rem;
                  }
                  .manual-content h3 {
                    font-size: 0.95rem; font-weight: 600; color: #52525b;
                    margin-top: 1rem; margin-bottom: 0.4rem;
                  }
                  .manual-content p { font-size: 0.875rem; line-height: 1.6; color: #3f3f46; margin: 0.5rem 0; }
                  .manual-content ul, .manual-content ol { padding-left: 1.5rem; margin: 0.5rem 0; font-size: 0.875rem; color: #3f3f46; }
                  .manual-content li { margin: 0.2rem 0; line-height: 1.5; }
                  .manual-content a { color: #b45309; text-decoration: none; }
                  .manual-content a:hover { text-decoration: underline; }
                  .manual-content code {
                    background: #f4f4f5; padding: 0.1rem 0.35rem; border-radius: 0.25rem;
                    font-size: 0.78rem; color: #18181b; font-family: ui-monospace, monospace;
                  }
                  .manual-content pre {
                    background: #18181b; color: #e4e4e7; padding: 0.75rem;
                    border-radius: 0.4rem; overflow-x: auto; font-size: 0.78rem;
                    margin: 0.5rem 0;
                  }
                  .manual-content pre code { background: transparent; color: inherit; padding: 0; }
                  .manual-content table {
                    border-collapse: collapse; width: 100%; margin: 0.6rem 0; font-size: 0.78rem;
                  }
                  .manual-content th {
                    background: #fef3c7; color: #78350f; font-weight: 600;
                    padding: 0.35rem 0.5rem; border: 1px solid #fde68a; text-align: left;
                  }
                  .manual-content td {
                    padding: 0.35rem 0.5rem; border: 1px solid #e4e4e7; color: #3f3f46;
                  }
                  .manual-content blockquote {
                    border-left: 4px solid #f59e0b; background: #fffbeb;
                    padding: 0.5rem 0.75rem; margin: 0.5rem 0; color: #52525b;
                    font-size: 0.875rem; font-style: normal;
                  }
                  .manual-content blockquote p { margin: 0; }
                  .manual-content hr { border: 0; border-top: 1px solid #e4e4e7; margin: 1.25rem 0; }
                  .manual-content strong { color: #18181b; font-weight: 600; }
                `}</style>
                <div
                  id="manual-content-root"
                  className="manual-content"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
